import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { conns } from '@cfg/axiosConfig.tsx';
import type { CommissionAgreement, CommissionScope, CommissionType } from '@app-types/staffCommission.ts';

export type { CommissionAgreement, CommissionScope, CommissionType } from '@app-types/staffCommission.ts';

export interface CommissionAgreementPayload {
  name: string;
  scope: CommissionScope;
  type: CommissionType;
  value: number;
  priority?: number;
  cap_per_line?: number | null;
  cap_per_order?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  default_agent_id?: number | null;
  category_ids?: number[] | null;
  item_ids?: number[] | null;
  exclusions?: number[] | null;
  is_active?: boolean;
}

export interface CommissionAgreementUpdatePayload extends Partial<CommissionAgreementPayload> {}

export type CommissionOverrideAction = 'override' | 'no_commission' | 'reset';

export interface CommissionOverrideRequest {
  action: CommissionOverrideAction;
  commission_amount?: number | null;
  agent_id?: number | null;
  reason: string;
  note?: string | null;
}

export interface SaleItemCommissionResponse {
  id: number;
  item_id: number;
  order_id: number;
  qty: number;
  total: number;
  commission: number;
  commission_user_id?: number | null;
  commission_agreement_id?: number | null;
  commission_is_manual: boolean;
  commission_metadata?: Record<string, unknown> | null;
}

const AGREEMENTS_QUERY_KEY = ['pos', 'commission-agreements'];

export const fetchCommissionAgreements = async (): Promise<CommissionAgreement[]> => {
  const { data } = await conns<CommissionAgreement[]>('erp:commissions:agreements').get();
  return data;
};

export const useCommissionAgreements = (): UseQueryResult<CommissionAgreement[], Error> => {
  return useQuery<CommissionAgreement[], Error>({
    queryKey: AGREEMENTS_QUERY_KEY,
    queryFn: fetchCommissionAgreements,
    staleTime: 60_000,
  });
};

const createCommissionAgreement = async (
  payload: CommissionAgreementPayload,
): Promise<CommissionAgreement> => {
  const { data } = await conns<CommissionAgreement>('erp:commissions:agreements').post(payload);
  return data;
};

const updateCommissionAgreement = async (
  id: number,
  payload: CommissionAgreementUpdatePayload,
): Promise<CommissionAgreement> => {
  const { data } = await conns<CommissionAgreement>('erp:commissions:agreements', [id]).patch(
    payload,
  );
  return data;
};

const deleteCommissionAgreement = async (id: number): Promise<void> => {
  await conns('erp:commissions:agreements', [id]).delete();
};

export const useCreateCommissionAgreement = (): UseMutationResult<
  CommissionAgreement,
  Error,
  CommissionAgreementPayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createCommissionAgreement,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: AGREEMENTS_QUERY_KEY });
    },
  });
};

export const useUpdateCommissionAgreement = (): UseMutationResult<
  CommissionAgreement,
  Error,
  { id: number; payload: CommissionAgreementUpdatePayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateCommissionAgreement(id, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: AGREEMENTS_QUERY_KEY });
    },
  });
};

export const useDeleteCommissionAgreement = (): UseMutationResult<void, Error, number> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteCommissionAgreement,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: AGREEMENTS_QUERY_KEY });
    },
  });
};

export const applyCommissionOverride = async (
  orderId: number,
  itemId: number,
  payload: CommissionOverrideRequest,
): Promise<SaleItemCommissionResponse> => {
  const { data } = await conns<SaleItemCommissionResponse>(
    'erp:orders:items:commission_override',
    [orderId, 'items', itemId, 'commission-override'],
  ).post(payload);
  return data;
};

export const useCommissionOverride = (): UseMutationResult<
  SaleItemCommissionResponse,
  Error,
  { orderId: number; itemId: number; payload: CommissionOverrideRequest }
> => {
  return useMutation({
    mutationFn: ({ orderId, itemId, payload }) => applyCommissionOverride(orderId, itemId, payload),
  });
};

